import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';

const partners = [
  {
    name: 'RevoUltra',
    tagline: 'Impermeabilizzazione definitiva',
    desc: 'Tecnologia nanopolimerica per impermeabilizzare tetti e coperture. Garanzia fino a 25 anni.',
    url: 'https://www.revoultra.it',
    logoType: 'text',
  },
  {
    name: 'Isoliamo',
    tagline: 'Isolamento tetti e intercapedini',
    desc: 'Isolamento termico di tetti e intercapedini con materiali certificati. Energia per la tua casa.',
    url: 'https://isoliamo.com',
    logoType: 'image',
    logoSrc: 'https://customer-assets.emergentagent.com/job_b2eca1db-26c1-43d9-9c75-7df69e4d27a2/artifacts/3qmu0ew3_5.png',
  },
  {
    name: 'RevoQuota',
    tagline: 'Lavori in quota certificati',
    desc: 'Specialisti nel lavoro in quota con sistemi di sicurezza avanzati.',
    url: 'https://revoquota.com',
    logoType: 'image',
    logoSrc: 'https://customer-assets.emergentagent.com/job_b2eca1db-26c1-43d9-9c75-7df69e4d27a2/artifacts/r9hxdhmu_2BF8C848-2593-4E9F-9AAB-F6A193DD2537.jpeg',
  },
];

const RevoUltraLogo = () => (
  <div className="flex items-baseline gap-1">
    <span className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit' }}>
      Revo
    </span>
    <span className="text-2xl font-bold tracking-tight text-[#15803d]" style={{ fontFamily: 'Outfit' }}>
      Ultra
    </span>
    <span className="text-xs text-[#15803d] font-semibold">®</span>
  </div>
);

const PartnersPreview = () => (
  <section className="py-24 md:py-32 bg-slate-50">
    <div className="max-w-7xl mx-auto px-6 md:px-8">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
        <span className="text-[#FF5722] text-sm font-semibold uppercase tracking-wider">Il nostro network</span>
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-3">Aziende Partner</h2>
        <p className="mt-4 text-slate-500 max-w-2xl mx-auto">Un ecosistema di aziende specializzate per un servizio completo e di altissima qualita.</p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {partners.map((partner, i) => (
          <motion.a
            key={partner.name}
            href={partner.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`partner-card-${partner.name.toLowerCase()}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="group bg-white border border-slate-200 rounded-2xl p-8 hover:shadow-xl hover:border-[#FF5722]/30 transition-all duration-300 flex flex-col"
          >
            <div className="h-20 flex items-center justify-start mb-5">
              {partner.logoType === 'text' ? (
                <RevoUltraLogo />
              ) : (
                <img
                  src={partner.logoSrc}
                  alt={`${partner.name} logo`}
                  className="max-h-16 w-auto object-contain"
                />
              )}
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>{partner.name}</h3>
            <p className="text-[#FF5722] text-sm font-medium mb-3">{partner.tagline}</p>
            <p className="text-sm text-slate-500 leading-relaxed flex-1">{partner.desc}</p>
            <div className="mt-5 inline-flex items-center gap-1.5 text-sm text-slate-700 group-hover:text-[#FF5722] font-medium transition-colors">
              Visita il sito
              <ExternalLink className="w-3.5 h-3.5" />
            </div>
          </motion.a>
        ))}
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mt-12 bg-[#0F172A] rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-2" data-testid="revoultra-banner">
        <div className="h-64 lg:h-auto overflow-hidden">
          <img src="/brochure/revoultra-applicazione.jpg" alt="Applicazione della membrana RevoUltra su tetto piano" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="p-8 lg:p-10">
          <div className="flex items-baseline gap-1 mb-3">
            <span className="text-2xl font-bold tracking-tight text-white" style={{ fontFamily: 'Outfit' }}>Revo</span>
            <span className="text-2xl font-bold tracking-tight text-emerald-400" style={{ fontFamily: 'Outfit' }}>Ultra</span>
            <span className="text-xs text-emerald-400 font-semibold">®</span>
          </div>
          <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>Protezione totale. Senza limiti.</h3>
          <p className="text-slate-300 text-sm leading-relaxed mb-5">Membrana impermeabilizzante in poliurea ad altissime prestazioni: applicazione continua senza giunti, resistente a grandine, raggi UV, pioggia e gelo. Ideale sottocoppo, sopracoppo e per tetti piani.</p>
          <div className="grid grid-cols-2 gap-2.5 mb-6">
            {['Impermeabile al 100%', 'Resistente alla grandine', 'Durevole nel tempo', 'Ideale per ogni tetto'].map((v) => (
              <div key={v} className="flex items-center gap-2 text-sm text-slate-200">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />{v}
              </div>
            ))}
          </div>
          <a href="https://www.revoultra.it" target="_blank" rel="noopener noreferrer" data-testid="revoultra-banner-link" className="inline-flex items-center gap-1.5 text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors">
            Scopri di più su revoultra.it <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

export default PartnersPreview;
